import type React from "react";
import { useMemo, useState } from "react";

import { type UpdateValvesRequestBody } from "@eisox/api/core-api";
import dayjs from "@eisox/dayjs";
import { Alert, NumberField as DSNumberField, Field, type AlertProps } from "@eisox/design-system";
import { useAppForm } from "@eisox/form";
import { getDirtyValues } from "@eisox/tools";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { valveApi } from "~/api";
import { Tooltip } from "~/components/tooltip";
import { VersionGate } from "~/components/version-gate";
import {
  calculateNewCorrectionCoef,
  calibrationSettingsSchema,
  getCalibrationData,
  type CalibrationSeverity,
} from "~/features/room/helpers";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";
import { useRoomContext } from "~/providers/room.provider";
import type { EnhancedValve } from "~/types/valve.type";

const NAME = "CalibrationSettings";

export const CalibrationSettings: React.FC = () => {
  const queryClient = useQueryClient();

  const { building, permissions, date } = useBuildingContext(NAME);
  const { room, valves } = useRoomContext(NAME);

  const [temperature, setTemperature] = useState<number | undefined>(room.temperature);

  const { mutateAsync: saveCalibration } = useMutation({
    mutationFn: (body: UpdateValvesRequestBody) => valveApi.update(building.id, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: valveApi.queries.building(building.id).queryKey });
    },
  });

  const { calibrableValves, severity } = getCalibrationData(
    valves,
    building.absenceTemperature,
    room.comfortTemperature,
    date,
    room.setpointTemp,
  );

  const defaultValves = calibrableValves.map(v => ({
    id: v.id,
    temperatureCorrectionCoef: v.commands?.temperatureCorrectionCoef ?? 0,
  }));

  const form = useAppForm({
    defaultValues: { valves: defaultValves },
    validators: {
      onSubmit: calibrationSettingsSchema,
    },
    onSubmit: async ({ value, formApi }) => {
      const dirty = getDirtyValues(formApi.options.defaultValues, value, { identity: "id" });
      if (!dirty) return;
      const body: UpdateValvesRequestBody = (dirty.valves ?? []).map(valve => ({
        ids: [valve.id],
        temperatureCorrectionCoef: valve.temperatureCorrectionCoef,
      }));
      await saveCalibration(body);
      setTemperature(undefined);
      formApi.reset({ valves: value.valves });
    },
  });

  const handleChangeTemperature = (value: number | null) => {
    if (value == null) return;
    setTemperature(value);
    const currentValves = form.getFieldValue("valves");
    const updated = currentValves.map(field => {
      const valve = valves.find(v => v.id === field.id);
      if (!valve?.correction || valve.sensors?.temperature === undefined) return field;
      const newCoef = calculateNewCorrectionCoef(
        valve.correction,
        value,
        valve.sensors.temperature,
        building.absenceTemperature,
        room.comfortTemperature,
      );
      if (newCoef === undefined) return field;
      return { ...field, temperatureCorrectionCoef: newCoef };
    });
    form.setFieldValue("valves", updated);
  };

  const severityConfig: Record<CalibrationSeverity, { severity: AlertProps["severity"]; label: string }> = useMemo(
    () => ({
      success: { severity: "success", label: m.calibration_temperature_good() },
      warning: {
        severity: "warning",
        label: `${m.calibration_temperature_warning_title()}\n${m.calibration_temperature_warning_comfort()}`,
      },
      heatSourceWarning: {
        severity: "warning",
        label: `${m.calibration_temperature_warning_title()}\n${m.calibration_temperature_warning_heat_source()}`,
      },
      correctionError: {
        severity: "error",
        label: `${m.calibration_temperature_error_title()}\n${m.calibration_temperature_error_correction()}`,
      },
      offlineError: {
        severity: "error",
        label: `${m.calibration_temperature_error_title()}\n${m.calibration_temperature_error_offline()}`,
      },
      mecaError: {
        severity: "error",
        label: `${m.calibration_temperature_error_title()}\n${m.calibration_temperature_error_meca()}`,
      },
      absenceTempError: {
        severity: "error",
        label: `${m.calibration_temperature_error_title()}\n${m.calibration_temperature_error_absence()}`,
      },
      error: { severity: "error", label: m.calibration_temperature_error_title() },
    }),
    [],
  );
  const calibrationStatus = severityConfig[severity];

  const syncedValvesCount = calibrableValves.filter(
    v => v.commands?.temperatureCorrectionCoef === v.stateData?.temperatureCorrectionCoef,
  ).length;

  const sortedValvesWithCalibrationUpdatedAt = calibrableValves
    .filter(
      (v): v is EnhancedValve & { commands: { temperatureCorrectionCoefUpdatedAt: string } } =>
        !!v.commands?.temperatureCorrectionCoefUpdatedAt,
    )
    .sort((a, b) =>
      b.commands.temperatureCorrectionCoefUpdatedAt.localeCompare(a.commands.temperatureCorrectionCoefUpdatedAt),
    );

  const valveVersions = valves.map(v => v.softwareVersion);
  const gatewayVersions = valves.map(v => v.gatewaySoftwareVersion);

  return (
    <form
      className="gap-2.5 flex h-full flex-col justify-between"
      onSubmit={e => {
        e.preventDefault();
        void form.handleSubmit();
      }}
      data-testid={NAME}
    >
      <form.AppForm>
        <div className="gap-2.5 flex flex-col">
          <Field.Root className="gap-2.5 flex flex-col">
            <Field.Label className="text-primary-blue">{m.calibration_temperature_label()}</Field.Label>
            <Alert severity="help" className="mb-2.5">
              {m.calibration_accounts_for_radiator_heat()}
            </Alert>
            <VersionGate
              devices={[
                {
                  type: "valve",
                  current: valveVersions,
                  feature: "TEMPERATURE_CORRECTION_COEF",
                },
                {
                  type: "gateway",
                  current: gatewayVersions,
                  feature: "TEMPERATURE_CORRECTION_COEF",
                },
              ]}
            >
              {({ disabled }) => (
                <div className="gap-2.5 flex w-full flex-col">
                  <DSNumberField.Root
                    value={temperature}
                    onValueChange={handleChangeTemperature}
                    disabled={
                      !permissions.room?.name?.update ||
                      calibrationStatus.severity === "error" ||
                      disabled ||
                      defaultValves.length === 0
                    }
                    step={0.1}
                  >
                    <DSNumberField.Group>
                      <DSNumberField.Decrement />
                      <DSNumberField.Input placeholder={m.temperature()} />
                      <DSNumberField.Increment />
                    </DSNumberField.Group>
                  </DSNumberField.Root>
                  {!disabled && (
                    <>
                      <Alert className="whitespace-pre-line" severity={calibrationStatus.severity}>
                        {calibrationStatus.label}
                      </Alert>
                      {syncedValvesCount !== calibrableValves.length && (
                        <Alert severity="warning">
                          {m.calibration_temperature_synced({
                            count: syncedValvesCount,
                            total: calibrableValves.length,
                          })}
                        </Alert>
                      )}
                    </>
                  )}
                </div>
              )}
            </VersionGate>
          </Field.Root>
          {sortedValvesWithCalibrationUpdatedAt[0] && (
            <Tooltip
              content={dayjs(
                sortedValvesWithCalibrationUpdatedAt[0].commands.temperatureCorrectionCoefUpdatedAt,
              ).format("L LT")}
            >
              <p className="w-fit text-base text-primary-dark-gray italic">
                {m.calibration_temperature_last_update({
                  date: dayjs(
                    sortedValvesWithCalibrationUpdatedAt[0].commands.temperatureCorrectionCoefUpdatedAt,
                  ).fromNow(),
                })}
              </p>
            </Tooltip>
          )}
        </div>

        <form.SubmitButton className="m-0 mx-auto" label={m.save()} />
      </form.AppForm>
    </form>
  );
};

CalibrationSettings.displayName = NAME;
